/* ========== SPARKLINE CHARTS ==========
 * Subtypes:
 *  - Line Sparkline (chartType='sparkline-line')
 *  - Column Sparkline (chartType='sparkline-column')
 *  - Win/Loss Sparkline (chartType='sparkline-winLoss')
 *
 * Sparklines render inline within their host cell — no axes, no legend,
 * no border.
 *
 * Colors come from the workbook (chartObj.sparkColors, parsed from the
 * x14:sparklineGroup): the series color paints the line/bars, negative
 * points use colorNegative, and the high/low/first/last/marker
 * highlights are drawn ONLY when their flag was enabled in Excel
 * (chartObj.sparkShow) — never invented.
 * ======================================== */

// Excel's defaults when the group carries no explicit colors.
var DEFAULT_SERIES   = '#376092'; // dark blue
var DEFAULT_NEGATIVE = '#D00000'; // red

export function drawSparklineChart(ctx, x, y, width, height, chartObj) {
  var series = chartObj && chartObj.series && chartObj.series[0] ? chartObj.series[0] : null;
  if (!series || !series.data || series.data.length === 0) return;
  var data = series.data;
  var subType = chartObj.chartType;

  var colors = chartObj.sparkColors || {};
  var show = chartObj.sparkShow || {};
  var seriesColor = colors.series || DEFAULT_SERIES;
  var negColor = colors.negative || DEFAULT_NEGATIVE;

  var maxVal = Math.max.apply(null, data);
  var minVal = Math.min.apply(null, data);
  var range = maxVal - minVal || 1;
  var n = data.length;

  ctx.clearRect(x, y, width, height);

  /** Per-point highlight color, honoring only the ENABLED flags. */
  function highlightColor(val, i) {
    if (show.negative && val < 0) return negColor;
    if (show.high && val === maxVal) return colors.high || negColor;
    if (show.low && val === minVal) return colors.low || negColor;
    if (show.first && i === 0) return colors.first || negColor;
    if (show.last && i === n - 1) return colors.last || negColor;
    return null;
  }

  if (subType === 'sparkline-column') {
    // Zero baseline when the data crosses zero (Excel behavior);
    // otherwise bars grow from the bottom of the cell.
    var hasNeg = minVal < 0;
    var top = y + 1;
    var bot = y + height - 1;
    var span = bot - top;
    var lo = hasNeg ? Math.min(0, minVal) : 0;
    var hi = Math.max(0, maxVal);
    var vRange = (hi - lo) || 1;
    var zeroY = bot - ((0 - lo) / vRange) * span;
    var binW = width / n;

    data.forEach(function (val, i) {
      var vy = bot - ((val - lo) / vRange) * span;
      var bx = x + i * binW;
      var hl = highlightColor(val, i);
      ctx.fillStyle = hl || (val < 0 ? negColor : seriesColor);
      if (val >= 0) {
        ctx.fillRect(bx + 1, vy, Math.max(1, binW - 2), Math.max(1, zeroY - vy));
      } else {
        ctx.fillRect(bx + 1, zeroY, Math.max(1, binW - 2), Math.max(1, vy - zeroY));
      }
    });
  } else if (subType === 'sparkline-winLoss') {
    // Fixed-height blocks above/below the midline; color marks the sign.
    var binW2 = width / n;
    var midY = y + height / 2;
    data.forEach(function (val, i) {
      if (val === 0) return;
      var bx = x + i * binW2;
      var hl = highlightColor(val, i);
      if (val > 0) {
        ctx.fillStyle = hl || seriesColor;
        ctx.fillRect(bx + 1, midY - (height / 2 - 2), Math.max(1, binW2 - 2), height / 2 - 2);
      } else {
        ctx.fillStyle = hl || negColor;
        ctx.fillRect(bx + 1, midY, Math.max(1, binW2 - 2), height / 2 - 2);
      }
    });
  } else {
    // ── Line sparkline ──
    var pointSpacing = width / Math.max(1, n - 1);
    var px = function (i) { return x + i * pointSpacing; };
    var py = function (val) { return y + (height - 2) - ((val - minVal) / range) * (height - 4); };

    ctx.strokeStyle = seriesColor;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    data.forEach(function (val, i) {
      if (i === 0) ctx.moveTo(px(i), py(val));
      else ctx.lineTo(px(i), py(val));
    });
    ctx.stroke();

    // Point markers — only the ones Excel was told to show.
    data.forEach(function (val, i) {
      var color = null;
      if (show.negative && val < 0) color = negColor;
      else if (show.high && val === maxVal) color = colors.high || negColor;
      else if (show.low && val === minVal) color = colors.low || negColor;
      else if (show.first && i === 0) color = colors.first || negColor;
      else if (show.last && i === n - 1) color = colors.last || negColor;
      else if (show.markers) color = colors.markers || seriesColor;
      if (!color) return;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px(i), py(val), 1.8, 0, Math.PI * 2);
      ctx.fill();
    });
  }
}
